import { useState, useReducer, useEffect, useCallback } from 'react';
import { useEscalationChainService } from '../services/escalationChainService';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';
import { RpcError } from '../../../shared/api/rpcClient';
import type { EscalationChain, EscalationLevel, ChannelStep, ChannelIntegration, ContactWithUser } from '../../../types';

interface ChainState {
  levels: EscalationLevel[];
  availableChannels: ChannelIntegration[];
  expandedLevel: number | null;
}

type ChainAction =
  | { type: 'LOAD'; payload: { levels: EscalationLevel[]; availableChannels: ChannelIntegration[] } }
  | { type: 'SET_EXPANDED'; payload: number | null }
  | { type: 'ADD_LEVEL'; payload: { levelType: 'cloud2' | 'customer' } }
  | { type: 'REMOVE_LEVEL'; payload: number }
  | { type: 'UPDATE_LEVEL'; payload: { index: number; updates: Partial<EscalationLevel> } }
  | { type: 'SET_CHANNEL_STEPS'; payload: { levelIndex: number; steps: ChannelStep[] } }
  | { type: 'REORDER_LEVELS'; payload: { fromIndex: number; toIndex: number } };

const initialState: ChainState = {
  levels: [],
  availableChannels: [],
  expandedLevel: null,
};

function chainReducer(state: ChainState, action: ChainAction): ChainState {
  switch (action.type) {
    case 'LOAD':
      return {
        ...state,
        levels: action.payload.levels,
        availableChannels: action.payload.availableChannels,
        expandedLevel: null,
      };

    case 'SET_EXPANDED':
      return { ...state, expandedLevel: action.payload };

    case 'ADD_LEVEL': {
      const isCloud2 = action.payload.levelType === 'cloud2';
      const newLevel: EscalationLevel = {
        level_number: state.levels.length,
        level_type: action.payload.levelType,
        name: isCloud2 ? 'Cloud2' : '',
        description: '',
        contact_username: null,
        channel_steps: [],
      };
      const levels = renumber([...state.levels, newLevel]);
      return { ...state, levels, expandedLevel: levels.length - 1 };
    }

    case 'REMOVE_LEVEL': {
      const level = state.levels[action.payload];
      if (!level) return state;
      // A chain must keep at least one Cloud2 step (mirrors backend guardrail).
      if (level.level_type === 'cloud2' && countCloud2(state.levels) <= 1) return state;
      const levels = renumber(state.levels.filter((_, i) => i !== action.payload));
      return { ...state, levels, expandedLevel: null };
    }

    case 'UPDATE_LEVEL': {
      const { index, updates } = action.payload;
      const level = state.levels[index];
      if (!level) return state;
      const newLevels = [...state.levels];
      newLevels[index] = { ...level, ...updates };
      return { ...state, levels: newLevels };
    }

    case 'SET_CHANNEL_STEPS': {
      const { levelIndex, steps } = action.payload;
      const level = state.levels[levelIndex];
      if (!level) return state;
      const newLevels = [...state.levels];
      newLevels[levelIndex] = { ...level, channel_steps: steps };
      return { ...state, levels: newLevels };
    }

    case 'REORDER_LEVELS': {
      const { fromIndex, toIndex } = action.payload;
      if (
        fromIndex < 0 || toIndex < 0 ||
        fromIndex >= state.levels.length || toIndex >= state.levels.length
      ) {
        return state;
      }
      const reordered = [...state.levels];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);
      return { ...state, levels: renumber(reordered), expandedLevel: null };
    }
  }
}

function countCloud2(levels: EscalationLevel[]): number {
  return levels.filter((l) => l.level_type === 'cloud2').length;
}

function renumber(levels: EscalationLevel[]): EscalationLevel[] {
  return levels.map((l, i) => ({ ...l, level_number: i }));
}

export function useEscalationChainTab(isActive: boolean, customerId: string, contacts: ContactWithUser[]) {
  const service = useEscalationChainService();
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const [state, dispatch] = useReducer(chainReducer, initialState);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadChain = useCallback(async () => {
    setLoading(true);
    try {
      const chain: EscalationChain = await service.getEscalationChain(customerId);
      dispatch({
        type: 'LOAD',
        payload: { levels: chain.levels, availableChannels: chain.available_channels },
      });
    } catch (err: unknown) {
      const msg = err instanceof RpcError ? err.message : 'Failed to load escalation chain';
      showSnackbar(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [service, customerId, showSnackbar]);

  useEffect(() => {
    if (isActive) {
      loadChain();
    }
  }, [isActive, loadChain]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const chain = await service.updateEscalationChain(customerId, state.levels);
      dispatch({
        type: 'LOAD',
        payload: { levels: chain.levels, availableChannels: chain.available_channels },
      });
      showSnackbar('Escalation chain saved', 'success');
    } catch (err: unknown) {
      const msg = err instanceof RpcError ? err.message : 'Failed to save escalation chain';
      showSnackbar(msg, 'error');
    } finally {
      setSaving(false);
    }
  }, [service, customerId, state.levels, showSnackbar]);

  const getContactName = useCallback(
    (username: string | null): string => {
      if (!username) return '';
      const contact = contacts.find((c) => c.username === username);
      return contact?.name || contact?.email || username;
    },
    [contacts],
  );

  const hasCloud2 = state.levels.some((l) => l.level_type === 'cloud2');

  return {
    state,
    dispatch,
    loading,
    saving,
    snackbar,
    closeSnackbar,
    handleSave,
    getContactName,
    hasCloud2,
  };
}

export { chainReducer, initialState };
export type { ChainState, ChainAction };
